import { apiDelete, apiGet, apiPost, request } from '@/shared/utils/requestBuilder';
import { lerDadosDaRespostaDoCore } from '@/shared/utils/respostaDoCore';
import { uploadParaUrlAssinada } from '@/shared/utils/uploadParaUrlAssinada';
import type { AIContext, PedidoSuggestion } from '@/features/processo-arbitral/types/conflictTypes';
import type { CustasPorRito } from '@/features/processo-arbitral/types/custasTypes';
import type {
  Contact,
  ContactData,
  ContactSearchResult,
} from '@/features/processo-arbitral/types/contactTypes';
import type {
  DocumentoPadraoNoRascunho,
  ProcessoArbitralDraftDocumentItem,
  ProcessoArbitralDraftUploadIntent,
  ProcessoArbitralDraftUploadItem,
  UploadFailure,
  UploadResult,
  UsarDocumentoPadraoNoRascunhoPayload,
} from '@/features/processo-arbitral/types/documentsTypes';

/**
 * Teto do uso de documento padrão: a operação trava o rascunho e copia um arquivo numa transação.
 * Sem ele, uma conexão presa deixava o painel em "confirmando" (SEC-01 do review do web#1358).
 */
const TIMEOUT_DO_USO_NO_RASCUNHO_MS = 30_000;

interface BackendResponse<T> {
  data?: T;
}

interface UploadController {
  aborted: boolean;
  abort?: () => void;
  uploadId?: string;
}

interface UploadDraftDocumentParams {
  draftId: string;
  uploadKey: string;
  file: File;
  categoria: 'comprovacao' | 'conflito';
  subtipo?:
    | 'PROCURACAO'
    | 'CARTEIRA_OAB'
    | 'CONTRATO_SOCIAL'
    | 'ANEXO_CONFLITO'
    | 'PETICAO_INICIAL';
  requirementId?: string;
  partyId?: string;
  partyName?: string;
  partyRole?: string;
  representedSide?: 'requerente' | 'requerido';
  descricao?: string;
  onProgress?: (progress: number) => void;
}

interface SuccessEnvelope<T> {
  success: boolean;
  data?: T;
  error?:
    | {
        message?: string;
      }
    | string;
  message?: string;
}

const retryFiles = new Map<string, File>();
const uploadControllers = new Map<string, UploadController>();

const createAbortedFailure = (): UploadFailure => ({
  code: 'aborted',
  message: 'Upload cancelado.',
});

const assertActiveUpload = (uploadKey: string): UploadController => {
  const controller = uploadControllers.get(uploadKey);

  if (!controller) {
    throw createAbortedFailure();
  }

  if (controller.aborted) {
    throw createAbortedFailure();
  }

  return controller;
};

const handleApiError = async (response: Response, defaultMessage: string): Promise<never> => {
  const body = (await response.json().catch(() => null)) as unknown;

  if (typeof body === 'object' && body !== null) {
    const record = body as Record<string, unknown>;
    const directMessage = record.message;
    const directError = record.error;

    if (typeof directMessage === 'string' && directMessage.trim()) {
      throw new Error(directMessage);
    }

    if (typeof directError === 'string' && directError.trim()) {
      throw new Error(directError);
    }

    if (typeof directError === 'object' && directError !== null) {
      const nestedError = directError as Record<string, unknown>;
      const nestedMessage = nestedError.message;

      if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
        throw new Error(nestedMessage);
      }
    }
  }

  throw new Error(defaultMessage);
};

const unwrapEnvelope = <T>(body: unknown): T => {
  if (typeof body === 'object' && body !== null && 'success' in body) {
    const envelope = body as SuccessEnvelope<T>;

    if (envelope.success && envelope.data !== undefined) {
      return envelope.data;
    }

    if (typeof envelope.error === 'string' && envelope.error.trim()) {
      throw new Error(envelope.error);
    }

    if (typeof envelope.error === 'object' && envelope.error?.message) {
      throw new Error(envelope.error.message);
    }

    throw new Error(envelope.message || 'Erro ao processar documento arbitral');
  }

  return body as T;
};

export const wizardStepService = {
  fetchRecentContacts: async (limit = 6): Promise<Contact[]> => {
    const response = await apiGet(`/partes/recentes?limit=${limit}`);

    if (!response.ok) {
      throw new Error('Erro ao carregar contatos recentes');
    }

    const body = (await response.json()) as BackendResponse<Contact[]>;

    return body.data ?? [];
  },

  searchContactByDocument: async (documento: string): Promise<ContactSearchResult> => {
    const response = await apiPost('/partes/buscar-documento', { documento });

    if (!response.ok) {
      throw new Error('Erro ao buscar contato');
    }

    const body = (await response.json()) as BackendResponse<ContactSearchResult>;

    if (!body.data) {
      throw new Error('Resposta inválida ao buscar contato');
    }

    return body.data;
  },

  /**
   * Bloqueio de duplicidade na criação: já existe processo arbitral em aberto (não
   * terminal) NESTE escritório para este CPF/CNPJ? Falha de rede/API não deve travar
   * quem está criando um processo legítimo — trata como "sem conflito conhecido".
   */
  verificarProcessoAbertoPorDocumento: async (cpfCnpj: string): Promise<boolean> => {
    const response = await apiGet(
      `/arbitral/processos/verificar-documento-aberto?cpfCnpj=${encodeURIComponent(cpfCnpj)}`,
    );

    if (!response.ok) {
      return false;
    }

    const body = (await response.json()) as BackendResponse<{ temProcessoAberto: boolean }>;

    return body.data?.temProcessoAberto ?? false;
  },

  /**
   * Custas da câmara por rito JÁ na ótica do escritório logado: preço da variação do rito com
   * o desconto do contrato do cliente aplicado, quando houver. Chaves no casing do enum do
   * backend (SUMARISSIMO, SUMARIO, ORDINARIO, ARBITRIO); rito sem variação cadastrada vem com
   * `valor: null`. É a MESMA conta que a cobrança faz — a tela e o boleto deixaram de poder
   * divergir (ClickUp 86akh5jdc).
   */
  fetchCustasPorRito: async (): Promise<CustasPorRito> => {
    const response = await apiGet('/arbitral/processos/custas-por-rito');

    if (!response.ok) {
      throw new Error('Erro ao carregar as custas da câmara');
    }

    const body = (await response.json()) as BackendResponse<CustasPorRito>;

    return body.data ?? {};
  },

  saveCatalogContact: async (contact: ContactData): Promise<Contact> => {
    const response = await apiPost('/partes/catalogo', {
      cpfCnpj: contact.cpfCnpj,
      nome: contact.nome,
      tipo: contact.tipo,
      ...(contact.email?.trim() ? { email: contact.email.trim() } : {}),
      ...(contact.telefone?.trim() ? { telefone: contact.telefone.trim() } : {}),
    });

    if (!response.ok) {
      throw new Error('Erro ao salvar contato no catálogo');
    }

    const body = (await response.json()) as BackendResponse<Contact>;

    if (!body.data) {
      throw new Error('Resposta inválida ao salvar contato');
    }

    return body.data;
  },

  generateAiSuggestions: async (context: AIContext): Promise<PedidoSuggestion[]> => {
    if (!context.breveRelato?.trim()) {
      throw new Error('Preencha o relato do conflito antes de gerar sugestões com IA.');
    }

    if (!context.requerente.trim() || !context.requerido.trim()) {
      throw new Error('Preencha requerente e requerido antes de gerar sugestões com IA.');
    }

    const response = await apiPost('/arbitral/processos/conflito/sugestoes-pedido', context);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao gerar sugestões de pedido');
    }

    const body = (await response.json()) as unknown;

    return unwrapEnvelope<PedidoSuggestion[]>(body);
  },

  /**
   * Envia o anexo direto ao storage e devolve o upload já confirmado na API.
   *
   * Três passos porque o arquivo não passa mais pelo backend: a API autoriza e
   * assina a URL, o browser faz o PUT no storage, a API confirma lendo a
   * metadata real do objeto. O PUT é XHR e não `fetch` por dois motivos que o
   * `fetch` não entrega: progresso de upload e cancelamento de verdade no meio
   * do envio — com 50MB, os dois importam.
   */
  uploadDraftDocument: async ({
    draftId,
    uploadKey,
    file,
    categoria,
    subtipo,
    requirementId,
    partyId,
    partyName,
    partyRole,
    representedSide,
    descricao,
    onProgress,
  }: UploadDraftDocumentParams): Promise<UploadResult> => {
    retryFiles.set(uploadKey, file);
    const controller: UploadController = { aborted: false };
    uploadControllers.set(uploadKey, controller);

    try {
      assertActiveUpload(uploadKey);
      onProgress?.(0);

      const intentResponse = await apiPost(
        `/arbitral/processos/rascunhos/${draftId}/documentos/upload-intent`,
        {
          nomeArquivo: file.name,
          tipoMime: file.type || 'application/octet-stream',
          tamanhoArquivo: file.size,
          categoria,
          ...(subtipo ? { subtipo } : {}),
          ...(requirementId ? { requirementId } : {}),
          ...(partyId ? { partyId } : {}),
          ...(partyName ? { partyName } : {}),
          ...(partyRole ? { partyRole } : {}),
          ...(representedSide ? { representedSide } : {}),
          ...(descricao ? { descricao } : {}),
        },
      );

      if (!intentResponse.ok) {
        await handleApiError(intentResponse, 'Erro ao autorizar envio do documento arbitral');
      }

      const intent = unwrapEnvelope<ProcessoArbitralDraftUploadIntent>(
        (await intentResponse.json()) as unknown,
      );
      controller.uploadId = intent.upload.id;

      assertActiveUpload(uploadKey);
      // Cancelar no meio do PUT rejeita com o mesmo UploadFailure do cancelamento antes dele.
      const abortDoPut = new AbortController();
      controller.abort = () => abortDoPut.abort(createAbortedFailure());
      await uploadParaUrlAssinada(intent, file, { onProgress, signal: abortDoPut.signal });
      assertActiveUpload(uploadKey);

      const confirmResponse = await apiPost(
        `/arbitral/processos/rascunhos/${draftId}/documentos/uploads/${intent.upload.id}/confirmar`,
        {},
      );

      if (!confirmResponse.ok) {
        await handleApiError(confirmResponse, 'Erro ao confirmar envio do documento arbitral');
      }

      const upload = unwrapEnvelope<ProcessoArbitralDraftUploadItem>(
        (await confirmResponse.json()) as unknown,
      );

      onProgress?.(100);

      return {
        uploadId: upload.id,
        status: upload.status,
        fileId: upload.documentoId,
        fileName: upload.nomeOriginal || upload.nomeArquivo,
        fileSize: upload.tamanhoArquivo,
        mimeType: upload.tipoMime || undefined,
        error: upload.mensagemErro,
      };
    } catch (error) {
      if (controller.uploadId) {
        await wizardStepService
          .cancelDraftUpload(draftId, controller.uploadId)
          .catch(() => undefined);
      }

      throw error;
    } finally {
      uploadControllers.delete(uploadKey);
    }
  },

  cancelUpload: (uploadKey: string): void => {
    const controller = uploadControllers.get(uploadKey);

    if (controller) {
      controller.aborted = true;
      controller.abort?.();
    }
  },

  getRetryFile: (uploadKey: string): File | null => retryFiles.get(uploadKey) ?? null,

  removeDraftDocument: async (draftId: string, documentId: string): Promise<void> => {
    const response = await apiDelete(
      `/arbitral/processos/rascunhos/${draftId}/documentos/${documentId}`,
    );

    if (!response.ok) {
      await handleApiError(response, 'Erro ao remover documento arbitral');
    }
  },

  cancelDraftUpload: async (draftId: string, uploadId: string): Promise<void> => {
    const response = await apiDelete(
      `/arbitral/processos/rascunhos/${draftId}/documentos/uploads/${uploadId}`,
    );

    if (response.status === 404) {
      return;
    }

    if (!response.ok) {
      await handleApiError(response, 'Erro ao cancelar envio do documento arbitral');
    }
  },

  listDraftDocuments: async (draftId: string): Promise<ProcessoArbitralDraftDocumentItem[]> => {
    const response = await apiGet(`/arbitral/processos/rascunhos/${draftId}/documentos`);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao listar documentos do rascunho arbitral');
    }

    return unwrapEnvelope<ProcessoArbitralDraftDocumentItem[]>((await response.json()) as unknown);
  },

  listDraftDocumentUploads: async (draftId: string): Promise<ProcessoArbitralDraftUploadItem[]> => {
    const response = await apiGet(`/arbitral/processos/rascunhos/${draftId}/documentos/uploads`);

    if (!response.ok) {
      await handleApiError(response, 'Erro ao listar uploads do rascunho arbitral');
    }

    return unwrapEnvelope<ProcessoArbitralDraftUploadItem[]>((await response.json()) as unknown);
  },

  retryDraftUpload: async (
    draftId: string,
    uploadId: string,
  ): Promise<ProcessoArbitralDraftUploadItem> => {
    const response = await apiPost(
      `/arbitral/processos/rascunhos/${draftId}/documentos/uploads/${uploadId}/retry`,
      {},
    );

    if (!response.ok) {
      await handleApiError(response, 'Erro ao reenfileirar upload do rascunho arbitral');
    }

    return unwrapEnvelope<ProcessoArbitralDraftUploadItem>((await response.json()) as unknown);
  },

  /**
   * Usa uma versão de documento padrão num destino da etapa Documentos. O core copia o arquivo e
   * grava o documento no rascunho numa transação só. Sem a recuperação de workspace no 403: ela
   * refaria o POST sem o header, e o painel precisa receber o "sem permissão" como veio.
   */
  usarDocumentoPadraoNoRascunho: async (
    draftId: string,
    payload: UsarDocumentoPadraoNoRascunhoPayload,
  ): Promise<DocumentoPadraoNoRascunho> =>
    lerDadosDaRespostaDoCore<DocumentoPadraoNoRascunho>(
      await request({
        endpoint: `/arbitral/processos/rascunhos/${draftId}/documentos-padrao`,
        method: 'POST',
        body: payload,
        recuperarWorkspaceNo403: false,
        signal: AbortSignal.timeout(TIMEOUT_DO_USO_NO_RASCUNHO_MS),
      }),
      'Não foi possível usar o documento padrão no cadastro',
    ),

  forgetUpload: (uploadKey: string): void => {
    retryFiles.delete(uploadKey);
    uploadControllers.delete(uploadKey);
  },
};
